import { Model } from "@/app/api/[[...route]]/services/ai/metrics";
import {
    CreateLomdaValues,
    GenerateLomdaValues,
    SceneTemplateVariation,
    SceneTemplate,
    VoiceToneType,
} from "./types";
import { Environment } from "@/app/api/[[...route]]/services/ai/generate-lomda/types";
import {
    templateNone,
    templateA,
    templateB,
    templateC,
    templateD,
    templateE,
    templateF,
    templateG,
    templateH,
    templateI,
} from "@/features/lomdaEditor/components/layout/templates";

const FUNNEL_FILE = {
    title: "יש לבחור קובץ לומדה",
    description:
        "יש לבחור קובץ לומדה כדי שהAI יוכל ליצור לומדה על סמך תוכן העמודים שבו, בשפת תסריט שתבחרו בשלב הבא.",
    subtitle: "קבצים נתמכים: PDF",
    loading: "טוען...",
};

const FUNNEL_SCRIPT = {
    title: "הגדרות תסריט הלומדה",
    languageLabel: "שפה",
    languagePlaceholder: "בחרו את השפה הרצוייה",
    languageDescription:
        "השפה שבה הAI יכתוב את התסריט ללומדה. על סמך תוכן הקובץ שבחרתם.",
    scriptLengthLabel: "אורך התסריט",
    scriptLengthPlaceholder: "בחרו את אורך התסריט",
    scriptLengthDescription: "אורך התסריט שהAI יכתוב לכל סצנה בלומדה.",
    voiceToneLabel: "טון הדיבור",
    voiceTonePlaceholder: "בחרו את טון הדיבור",
    voiceToneDescription: "סגנון הדיבור שהAI יכתוב את תסריט הסצנות בלומדה.",
};

const FUNNEL_BRANDING = {
    title: "הגדרות מיתוג הלומדה",
    primaryColorLabel: "צבע ראשי",
    secondaryColorLabel: "צבע משני",
    colorsDescription:
        "נא לבחור צמד צבעים לשימוש בתבניות הלומדה. (טיפ: צבעי המיתוג שלכם)",
    logoLabel: "לוגו",
    logoDescription: "אפשר להעלות לוגו כדי שיהיה מוצג בתבניות הלומדה.",
    templateLabel: "תבנית סצנה",
    templateDescription: "תבנית המגדירה את מראה הסצנה",
    loading: "טוען...",
};

const FUNNEL_STORAGE = {
    title: "הגדרות אחסון",
    nameLabel: "שם הלומדה",
    namePlaceholder: "שם פריט הלומדה",
    nameDescription: "פריט הלומדה יופיע תחת השם הזה באזור האחסון.",
    folderLabel: "תיקייה",
    folderPlaceholder: "לחצו כדי לבחור תיקייה",
    chooseFolderButton: "בחירה",
    folderDescription: "תיקייה בה תוכלו למצוא את הלומדה החדשה באזור האחסון",
    loading: "טוען...",
};

const FUNNEL_ERRORS = {
    fileTooLong: "הקובץ שבחרתם ארוך מידי, האורך המקסימלי הוא 50 עמודים.",
    unexpectedError: "קרתה שגיאה לא צפויה",
    generalError: "קרתה שגיאה",
};

const FUNNEL_PROCESSING = {
    title: "הלומדה שלך כמעט מוכנה",
    generating:
        "תהליך הייצור יכול לקחת כמה דקות, תלוי בגודל הקובץ ובכמות הסצנות בלומדה.",
    creating: "מכין כמה דברים חשובים...",
};

const FUNNEL_SUCCESS = {
    message: "הלומדה נוצרה בהצלחה!",
};

export const GENERATE_FUNNEL_TEXTS = {
    step1: FUNNEL_FILE,
    step2: FUNNEL_BRANDING,
    step3: FUNNEL_SCRIPT,
    step4: FUNNEL_STORAGE,
    step5: FUNNEL_PROCESSING,
    errors: FUNNEL_ERRORS,
    success: FUNNEL_SUCCESS,
    AI: {
        text: "דילוג על AI",
    },
} as const;

export const CREATE_FUNNEL_TEXTS = {
    step1: FUNNEL_BRANDING,
    step2: FUNNEL_STORAGE,
    step3: FUNNEL_PROCESSING,
    errors: FUNNEL_ERRORS,
    success: FUNNEL_SUCCESS,
    AI: {
        text: "צור מקובץ עם AI",
    },
} as const;

export const UPLOAD_CONFIG = {
    pdf: {
        maxFileSize: 50, // MB
        allowedTypes: ["PDF"],
    },
    logo: {
        maxFileSize: 5, // MB
        allowedTypes: ["PNG", "SVG", "JPG", "WEBP"],
    },
} as const;

export const DEFAULT_COLORS = {
    primary: "#000f5e",
    secondary: "#c4d8fb",
} as const;

export const DEFAULT_VOICE_TONE: VoiceToneType = "educational";

export const DEFAULT_LOMDA_SCRIPT_SETTINGS = {
    language: "he",
    scriptLength: "asis",
    voiceTone: DEFAULT_VOICE_TONE,
} as const;

export const DEFAULT_LOMDA_TEMPLATE: SceneTemplateVariation = "none";
export const DEFAULT_LOMDA_ASPECT_RATIO = "16:9";

export const GENERATE_INITIAL_VALUES: GenerateLomdaValues = {
    file: undefined,
    name: undefined,
    language: DEFAULT_LOMDA_SCRIPT_SETTINGS.language,
    scriptLength: DEFAULT_LOMDA_SCRIPT_SETTINGS.scriptLength,
    voiceTone: DEFAULT_LOMDA_SCRIPT_SETTINGS.voiceTone,
    colors: DEFAULT_COLORS,
    logo: undefined,
    template: DEFAULT_LOMDA_TEMPLATE,
    aspectRatio: DEFAULT_LOMDA_ASPECT_RATIO,
    folderId: undefined,
    lomdaId: "",
};

export const CREATE_INITIAL_VALUES: CreateLomdaValues = {
    name: undefined,
    colors: DEFAULT_COLORS,
    logo: undefined,
    template: DEFAULT_LOMDA_TEMPLATE,
    aspectRatio: DEFAULT_LOMDA_ASPECT_RATIO,
    folderId: undefined,
    lomdaId: "",
};

export const DEFAULT_LOMDA_METADATA = {
    numOfScenes: 0,
    aspectRatio: DEFAULT_LOMDA_ASPECT_RATIO,
    language: DEFAULT_LOMDA_SCRIPT_SETTINGS.language,
    scriptLength: DEFAULT_LOMDA_SCRIPT_SETTINGS.scriptLength,
    voiceTone: DEFAULT_LOMDA_SCRIPT_SETTINGS.voiceTone,
    colors: DEFAULT_COLORS,
    logoUrl: "",
    thumbnailUrl: "",
};

export const DEFAULT_LOMDA_CONTENT = {
    scenes: [],
};

export const MODEL_OPENAI: Model = "gpt-4o";
export const MODEL_GOOGLE: Model = "gemini-2.5-flash";

export const SCENE_LIMIT = 50;

export const RASTER_CONFIG = {
    dpi: 96,
    env: (process.env.NEXT_PUBLIC_ENV !== "prod"
        ? "dev"
        : "prod") as Environment,
};

export const TEMPLATE_RADIUS_CONSTANTS = {
    V: 2.78,
    H: 1.56,
    R: 30,
};

export const SCENE_TEMPLATES: Record<SceneTemplateVariation, SceneTemplate> = {
    none: templateNone,
    A: templateA,
    B: templateB,
    C: templateC,
    D: templateD,
    E: templateE,
    F: templateF,
    G: templateG,
    H: templateH,
    I: templateI,
};
